import { Container } from '../types/canvas.types';

/**
 * Search result with parent information
 */
export interface ContainerSearchResult {
    container: Container | null;
    parentId: string | null;
    parentContainer: Container | null;
    path: string[];
}

/**
 * Find a container by ID (simple search)
 * @param containers - Array of containers to search
 * @param targetId - ID of the container to find
 * @returns Found container or null
 */
export function findContainerById(
    containers: Container[],
    targetId: string
): Container | null {
    for (const container of containers) {
        if (container.id === targetId) {
            return container;
        }

        if (container.content.nestedContainers && container.content.nestedContainers.length > 0) {
            const found = findContainerById(container.content.nestedContainers, targetId);
            if (found) return found;
        }
    }
    return null;
}

/**
 * Find a container with its parent information
 * @param containers - Array of containers to search
 * @param targetId - ID of the container to find
 * @param parentId - Current parent ID (used internally for recursion)
 * @returns Search result with container, parent, and path information
 */
export function findContainerWithParent(
    containers: Container[],
    targetId: string,
    parentId: string | null = null,
    parentContainer: Container | null = null,
    currentPath: string[] = []
): ContainerSearchResult {
    for (const container of containers) {
        const containerPath = [...currentPath, container.id];

        if (container.id === targetId) {
            return {
                container,
                parentId,
                parentContainer,
                path: containerPath
            };
        }

        if (container.content.nestedContainers && container.content.nestedContainers.length > 0) {
            const result = findContainerWithParent(
                container.content.nestedContainers,
                targetId,
                container.id,
                container,
                containerPath
            );
            if (result.container) {
                return result;
            }
        }
    }

    return {
        container: null,
        parentId: null,
        parentContainer: null,
        path: []
    };
}

/**
 * Find the full path to a container (array of container IDs from root to target)
 * @param containers - Array of containers to search
 * @param targetId - ID of the container to find
 * @param currentPath - Current path (used internally for recursion)
 * @returns Array of container IDs forming the path, or null if not found
 */
export function findContainerPath(
    containers: Container[],
    targetId: string,
    currentPath: string[] = []
): string[] | null {
    for (const container of containers) {
        const containerPath = [...currentPath, container.id];

        if (container.id === targetId) {
            return containerPath;
        }

        if (container.content.nestedContainers && container.content.nestedContainers.length > 0) {
            const nestedPath = findContainerPath(
                container.content.nestedContainers,
                targetId,
                containerPath
            );
            if (nestedPath) return nestedPath;
        }
    }
    return null;
}

/**
 * Find all containers in the same row as the target container
 * @param containers - Array of containers to search
 * @param targetId - ID of the target nested container
 * @returns Array of containers in the same row
 */
export function findContainersInSameRow(
    containers: Container[],
    targetId: string
): Container[] {
    for (const container of containers) {
        if (container.content.nestedContainers && container.content.nestedContainers.length > 0) {
            // Find the target container in nested containers
            const targetContainer = container.content.nestedContainers.find(nc => nc.id === targetId);

            if (targetContainer) {
                const targetRowIndex = targetContainer.metadata?.rowIndex ?? 0;
                return container.content.nestedContainers.filter(
                    nc => (nc.metadata?.rowIndex ?? 0) === targetRowIndex
                );
            }

            const result = findContainersInSameRow(container.content.nestedContainers, targetId);
            if (result.length > 0) {
                return result;
            }
        }
    }
    return [];
}

/**
 * Get the parent container ID of a nested container
 * @param containers - Array of containers to search
 * @param nestedContainerId - ID of the nested container
 * @returns Parent container ID or null
 */
export function getParentContainerId(
    containers: Container[],
    nestedContainerId: string
): string | null {
    for (const container of containers) {
        if (container.metadata?.parentContainerId === nestedContainerId) {
            return container.metadata.parentContainerId;
        }

        if (container.content.nestedContainers && container.content.nestedContainers.length > 0) {
            const nestedIds = container.content.nestedContainers.map(nc => nc.id);
            if (nestedIds.includes(nestedContainerId)) {
                return container.id;
            }

            const parentId = getParentContainerId(container.content.nestedContainers, nestedContainerId);
            if (parentId) return parentId;
        }
    }
    return null;
}

/**
 * Find all containers at a specific nesting level
 * @param containers - Array of containers to search
 * @param targetLevel - Target nesting level (0 = top level, 1 = first nested, etc.)
 * @param currentLevel - Current level (used internally)
 * @returns Array of containers at the target level
 */
export function findContainersByLevel(
    containers: Container[],
    targetLevel: number,
    currentLevel: number = 0
): Container[] {
    if (currentLevel === targetLevel) {
        return containers;
    }

    const result: Container[] = [];
    for (const container of containers) {
        if (container.content.nestedContainers && container.content.nestedContainers.length > 0) {
            result.push(
                ...findContainersByLevel(
                    container.content.nestedContainers,
                    targetLevel,
                    currentLevel + 1
                )
            );
        }
    }
    return result;
}

/**
 * Count total number of containers (including nested)
 * @param containers - Array of containers to count
 * @returns Total count
 */
export function countContainers(containers: Container[]): number {
    let count = containers.length;
    for (const container of containers) {
        if (container.content.nestedContainers && container.content.nestedContainers.length > 0) {
            count += countContainers(container.content.nestedContainers);
        }
    }
    return count;
}

/**
 * Get maximum nesting depth
 * @param containers - Array of containers to analyze
 * @param currentDepth - Current depth (used internally)
 * @returns Maximum nesting depth
 */
export function getMaxNestingDepth(
    containers: Container[],
    currentDepth: number = 0
): number {
    let maxDepth = currentDepth;
    for (const container of containers) {
        if (container.content.nestedContainers && container.content.nestedContainers.length > 0) {
            const nestedDepth = getMaxNestingDepth(
                container.content.nestedContainers,
                currentDepth + 1
            );
            maxDepth = Math.max(maxDepth, nestedDepth);
        }
    }
    return maxDepth;
}
